import { UiNode } from '@ory/client';
import { Divider, PasswordInput, Stack, Text } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { Button } from 'src/components';
import { AuthFeedback } from 'src/auth/components/AuthFeedback';
import { AuthCard } from 'src/auth/components/AuthCard';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import {
  PasswordRequirements,
  passwordSchema,
  allPasswordRequirementsMet,
  PasswordFormValues,
} from 'src/components/convenience/auth/PasswordRequirements';
import { OidcSection } from 'src/components/convenience/auth/OidcSection';
import { SettingsPageConfig } from 'src/auth/settings/types';

interface ChangePasswordFormProps {
  config: SettingsPageConfig;
  error: string | null;
  isSubmitting: boolean;
  onSubmit: (password: string) => void;
  oidcNodes: UiNode[];
  formAction: string;
  csrfToken: string;
}

export const ChangePasswordForm = ({
  config,
  error,
  isSubmitting,
  onSubmit,
  oidcNodes,
  formAction,
  csrfToken,
}: ChangePasswordFormProps) => {
  const form = useForm<PasswordFormValues>({
    mode: 'controlled',
    initialValues: {
      password: '',
    },
    validate: zodResolver(passwordSchema),
    validateInputOnChange: true,
  });

  const password = form.values.password;
  const requirementsMet = allPasswordRequirementsMet(password);

  const handleSubmit = (values: PasswordFormValues) => {
    onSubmit(values.password);
  };

  return (
    <AuthCard>
      <Stack gap="md">
        <Text fz="xl" fw={FONT_WEIGHTS.semibold} ta="center">
          {config.pageTitle}
        </Text>

        {config.showPasswordSection && (
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              {config.passwordSectionSubtitle && (
                <Text size="sm" fw={FONT_WEIGHTS.medium} ta="center">
                  {config.passwordSectionSubtitle}
                </Text>
              )}

              <PasswordInput
                label={config.passwordInputLabel}
                placeholder={config.passwordInputPlaceholder}
                withAsterisk
                autoFocus
                autoComplete="new-password"
                key={form.key('password')}
                {...form.getInputProps('password')}
                error={false}
              />

              <PasswordRequirements password={password} />

              <AuthFeedback error={error} />

              <Button
                type="submit"
                fullWidth
                loading={isSubmitting}
                disabled={!requirementsMet || isSubmitting}
                variant="primary"
              >
                {config.submitButtonText}
              </Button>
            </Stack>
          </form>
        )}

        {config.showDivider && <Divider />}

        {config.showSsoSection && (
          <Stack gap="sm">
            <Text size="sm" fw={FONT_WEIGHTS.medium} ta="center">
              {config.ssoSectionSubtitle}
            </Text>

            <OidcSection nodes={oidcNodes} formAction={formAction} csrfToken={csrfToken} />
          </Stack>
        )}
      </Stack>
    </AuthCard>
  );
};
